// Editing-side helpers for the super-admin knowledge-import settings: the client-wide consent
// providers and browser steps on the chat bot (chatBot.knowledgeIngest), and a KB document's own
// browserSteps. Bounds and the exact-keys rule mirror helpers/knowledge/browser-steps.js in
// magellan-api, which validates everything again — these only let a super admin see a problem
// before the API answers 400 (same posture as helpers/funnels.js).

import {
  toAgentFieldMappingPayload,
  toAgentPlaybookPayload,
  toPriceDisplayRulePayload,
  validateAgentFieldMapping,
  validateAgentPlaybook,
  validatePriceDisplayRule,
} from './agentFieldMapping';
import { toAgentUiPayload, validateAgentUi } from './agentUi';

export const MAX_CONSENT_PROVIDERS = 50;
export const MAX_PROVIDER_NAME_LENGTH = 80;
export const MIN_PROVIDER_SELECTORS = 1;
export const MAX_PROVIDER_SELECTORS = 10;
export const MAX_BROWSER_STEPS = 20;
export const MAX_SELECTOR_LENGTH = 500;
// No 0: in puppeteer a 0 timeout means "wait forever", and an ingest job has no overall timeout.
export const STEP_TIMEOUT_MS = { min: 100, max: 10000, default: 3000 };
export const WAIT_MS = { min: 1, max: 5000, default: 1000 };

export const BROWSER_STEP_TYPES = [
  { value: 'waitForSelector', label: 'Wait for element' },
  { value: 'click', label: 'Click' },
  { value: 'wait', label: 'Wait' },
  { value: 'remove', label: 'Remove from page' },
];

// Besides `type`, the only keys the API takes per step type — any other key is a 400.
const STEP_FIELDS = {
  waitForSelector: ['selector', 'timeoutMs'],
  click: ['selector', 'timeoutMs'],
  wait: ['ms'],
  remove: ['selector'],
};

export const stepFields = (type) => STEP_FIELDS[type] || [];

/** Whether the API sent usable settings — an API from before the column sends nothing. */
export const hasKnowledgeIngest = (value) =>
  Boolean(value) && Array.isArray(value.consentProviders) && Array.isArray(value.browserSteps);

/**
 * `step` as a `type` step: fields both types have (selector, timeout) carry over, the rest are
 * dropped, and a field the new type needs starts at its default. So a row only ever holds its
 * type's keys and can go to the API as-is.
 */
export const changeBrowserStepType = (step, type) => {
  const fields = stepFields(type);
  const next = { type };
  if (fields.includes('selector')) next.selector = typeof step.selector === 'string' ? step.selector : '';
  if (fields.includes('timeoutMs')) next.timeoutMs = step.timeoutMs ?? STEP_TIMEOUT_MS.default;
  if (fields.includes('ms')) next.ms = step.ms ?? WAIT_MS.default;
  return next;
};

export const blankBrowserStep = () => changeBrowserStepType({}, 'remove');
export const blankConsentProvider = () => ({ name: '', selectors: [''] });

// Puppeteer-only syntax fails a CSS parse anyway; matched only to say why.
const PUPPETEER_SYNTAX = /^\s*(text|pierce|aria|xpath)\/|::-p-|>>>/i;

/** Why `selector` can't be saved, or null. */
export const selectorError = (selector) => {
  if (typeof selector !== 'string' || !selector.trim()) return 'Enter a CSS selector';
  if (selector.trim().length > MAX_SELECTOR_LENGTH) return `At most ${MAX_SELECTOR_LENGTH} characters`;
  // jsdom (and so the API's check) parses :contains( but Chrome does not — the API refuses it by name.
  if (/:contains\(/i.test(selector)) return ':contains( is not CSS — match a class, id or attribute instead';
  try {
    document.createDocumentFragment().querySelector(selector);
  } catch {
    return PUPPETEER_SYNTAX.test(selector)
      ? 'Plain CSS only — Puppeteer syntax (text/, pierce/, ::-p-, >>>) is not supported'
      : 'Not a valid CSS selector';
  }
  return null;
};

const wholeMsError = (value, { min, max }, noun) =>
  Number.isInteger(value) && value >= min && value <= max
    ? null
    : `${noun} must be a whole number from ${min} to ${max} ms`;

/** Per-field problems of one step: { type?, selector?, timeoutMs?, ms? } (null = fine). */
export const browserStepErrors = (step) => {
  if (!STEP_FIELDS[step?.type]) return { type: 'Pick an action' };
  const fields = stepFields(step.type);
  const errors = {};
  if (fields.includes('selector')) errors.selector = selectorError(step.selector);
  if (fields.includes('timeoutMs')) errors.timeoutMs = wholeMsError(step.timeoutMs, STEP_TIMEOUT_MS, 'Timeout');
  if (fields.includes('ms')) errors.ms = wholeMsError(step.ms, WAIT_MS, 'Duration');
  return errors;
};

/** First reason `steps` can't be saved, or null. */
export const validateBrowserSteps = (steps) => {
  if (!Array.isArray(steps)) return 'Browser steps are missing';
  if (steps.length > MAX_BROWSER_STEPS) return `At most ${MAX_BROWSER_STEPS} browser steps`;
  for (const [index, step] of steps.entries()) {
    const message = Object.values(browserStepErrors(step)).find(Boolean);
    if (message) return `Step ${index + 1}: ${message}`;
  }
  return null;
};

const trimmedName = (provider) => (typeof provider?.name === 'string' ? provider.name.trim() : '');

/** Trimmed names used by more than one provider (exact, case-sensitive — as the API compares). */
export const duplicateProviderNames = (providers) => {
  const seen = new Set();
  const duplicates = new Set();
  providers.forEach((provider) => {
    const name = trimmedName(provider);
    if (!name) return;
    if (seen.has(name)) duplicates.add(name);
    seen.add(name);
  });
  return duplicates;
};

/** Per-field problems of one provider: { name, count, selectors: [message|null] }. */
export const consentProviderErrors = (provider, duplicates = new Set()) => {
  const name = trimmedName(provider);
  const selectors = Array.isArray(provider?.selectors) ? provider.selectors : [];
  let nameError = null;
  if (!name) nameError = 'Enter a name';
  else if (name.length > MAX_PROVIDER_NAME_LENGTH) nameError = `At most ${MAX_PROVIDER_NAME_LENGTH} characters`;
  else if (duplicates.has(name)) nameError = 'Another provider has this name';
  let count = null;
  if (selectors.length < MIN_PROVIDER_SELECTORS) count = 'Add at least one selector';
  else if (selectors.length > MAX_PROVIDER_SELECTORS) count = `At most ${MAX_PROVIDER_SELECTORS} selectors`;
  return { name: nameError, count, selectors: selectors.map(selectorError) };
};

/** First reason `providers` can't be saved, or null. */
export const validateConsentProviders = (providers) => {
  if (!Array.isArray(providers)) return 'Consent providers are missing';
  if (providers.length > MAX_CONSENT_PROVIDERS) return `At most ${MAX_CONSENT_PROVIDERS} providers`;
  const duplicates = duplicateProviderNames(providers);
  for (const [index, provider] of providers.entries()) {
    const label = trimmedName(provider) ? `“${trimmedName(provider)}”` : `${index + 1}`;
    const errors = consentProviderErrors(provider, duplicates);
    if (errors.name) return `Provider ${label}: ${errors.name}`;
    if (errors.count) return `Provider ${label}: ${errors.count}`;
    const bad = errors.selectors.findIndex(Boolean);
    if (bad >= 0) return `Provider ${label}, selector ${bad + 1}: ${errors.selectors[bad]}`;
  }
  return null;
};

export const validateKnowledgeIngest = (knowledgeIngest) => {
  const providers = validateConsentProviders(knowledgeIngest?.consentProviders);
  if (providers) return `Consent banner providers: ${providers}`;
  const steps = validateBrowserSteps(knowledgeIngest?.browserSteps);
  if (steps) return `Client-wide browser steps: ${steps}`;
  return null;
};

/** Steps as the API takes them: exactly the type's keys, selectors trimmed. */
export const toBrowserStepsPayload = (steps) =>
  steps.map((step) =>
    stepFields(step.type).reduce(
      (payload, field) => ({ ...payload, [field]: field === 'selector' ? step.selector.trim() : step[field] }),
      { type: step.type }
    )
  );

/**
 * The settings as the API takes them: exactly consentProviders and browserSteps, each provider
 * exactly { name, selectors } — any other key is a 400. Trimmed here because the API never trims.
 */
export const toKnowledgeIngestPayload = ({ consentProviders, browserSteps }) => ({
  consentProviders: consentProviders.map(({ name, selectors }) => ({
    name: name.trim(),
    selectors: selectors.map((selector) => selector.trim()),
  })),
  browserSteps: toBrowserStepsPayload(browserSteps),
});

// Same comparison the page's own dirty check uses.
export const knowledgeIngestChanged = (chatBot, initialChatBot) =>
  JSON.stringify(chatBot?.knowledgeIngest) !== JSON.stringify(initialChatBot?.knowledgeIngest);

const fieldChanged = (chatBot, initialChatBot, field) =>
  JSON.stringify(chatBot?.[field]) !== JSON.stringify(initialChatBot?.[field]);

/**
 * What ChatBotPage's Save PUTs: { body } or { error }. The same posture for every privileged
 * (super-admin only) value — knowledgeIngest, agentFieldMapping, priceDisplayRule, agentPlaybook, agentUi:
 * - Unchanged: sent exactly as loaded — a restatement the API accepts from anyone, which is what
 *   keeps a non-super-admin's save clear of the 403. Never undefined, which would read as absent.
 * - Changed (only a super admin can, nobody else renders the editors): validated, then trimmed.
 * - knowledgeIngest with no usable value (absent, null, partial): the key is left out — absent
 *   means "leave it alone" to the API, while a null restated would be a 400 on every save. The
 *   others are left alone only when the API never sent them (key absent).
 */
export const chatBotSaveBody = (chatBot, initialChatBot) => {
  const { knowledgeIngest, ...rest } = chatBot;
  let body = chatBot;
  if (!hasKnowledgeIngest(knowledgeIngest)) {
    body = rest;
  } else if (knowledgeIngestChanged(chatBot, initialChatBot)) {
    const error = validateKnowledgeIngest(knowledgeIngest);
    if (error) return { error };
    body = { ...body, knowledgeIngest: toKnowledgeIngestPayload(knowledgeIngest) };
  }
  if ('agentFieldMapping' in chatBot && fieldChanged(chatBot, initialChatBot, 'agentFieldMapping')) {
    const error = validateAgentFieldMapping(chatBot.agentFieldMapping);
    if (error) return { error };
    body = { ...body, agentFieldMapping: toAgentFieldMappingPayload(chatBot.agentFieldMapping) };
  }
  if ('priceDisplayRule' in chatBot && fieldChanged(chatBot, initialChatBot, 'priceDisplayRule')) {
    const error = validatePriceDisplayRule(chatBot.priceDisplayRule);
    if (error) return { error };
    body = { ...body, priceDisplayRule: toPriceDisplayRulePayload(chatBot.priceDisplayRule) };
  }
  if ('agentPlaybook' in chatBot && fieldChanged(chatBot, initialChatBot, 'agentPlaybook')) {
    const error = validateAgentPlaybook(chatBot.agentPlaybook);
    if (error) return { error };
    body = { ...body, agentPlaybook: toAgentPlaybookPayload(chatBot.agentPlaybook) };
  }
  if ('agentUi' in chatBot && fieldChanged(chatBot, initialChatBot, 'agentUi')) {
    const error = validateAgentUi(chatBot.agentUi);
    if (error) return { error };
    body = { ...body, agentUi: toAgentUiPayload(chatBot.agentUi) };
  }
  return { body };
};
